import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Field } from './Modal';
import { Chip } from './StatusChip';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate, plural } from '../utils/format';
import { checkEmail, checkUkPhone, checkPostcode } from '../utils/clientSectionSpecs';

/**
 * <ClientOnboarding /> — onboard a client by confirmation, not data entry.
 *
 * The client is the least motivated party in the chain, so handing them a blank
 * form is how onboarding stalls. Fill in what you already know, send the link,
 * and all they do is check it and press confirm.
 */
const INIT = { client_id: '', email: '', companyName: '', contactName: '', phone: '', address: '', postcode: '', website: '', expires_days: '21' };
const EXPIRY_OPTIONS = [['7', '7 days'], ['14', '14 days'], ['21', '21 days'], ['30', '30 days'], ['60', '60 days'], ['90', '90 days']];
const isExpired = (i) => i.status === 'sent' && i.expires_at && new Date(i.expires_at) < new Date();
const chipFor = (i) => {
  if (i.status === 'confirmed') return ['success', 'Confirmed'];
  if (i.status === 'revoked') return ['muted', 'Withdrawn'];
  if (isExpired(i)) return ['warning', 'Expired'];
  return ['info', 'Awaiting confirmation'];
};

const copyText = async (text) => {
  if (!text || !navigator.clipboard?.writeText) return false;
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
};

const ClientOnboarding = () => {
  const { confirm } = useConfirm();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(INIT);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [manualLink, setManualLink] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await apiFetch('/api/contractor/client-onboarding'); setInvites(d?.invites || []); setError(null); }
    catch (e) { setError(e); }
    finally { setLoading(false); }
    try { const d = await apiFetch('/api/contractor/clients'); setClients(Array.isArray(d?.clients) ? d.clients : (Array.isArray(d) ? d : [])); }
    catch { setClients([]); }   // the "start from a saved client" picker is optional
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((x) => ({ ...x, [k]: null })); };
  // Picking a saved client pre-fills everything we already hold on them.
  const pickClient = (id) => {
    const c = clients.find((x) => String(x.id) === String(id));
    setForm((f) => ({
      ...f, client_id: id,
      companyName: c?.name || '', contactName: c?.contact_name || '', email: c?.contact_email || f.email,
      phone: c?.contact_phone || '', address: c?.address || '', postcode: c?.postcode || '', website: c?.website || '',
    }));
    setErrors({});
  };

  const cancel = async () => {
    const dirty = JSON.stringify(form) !== JSON.stringify(INIT);
    if (dirty && !(await confirm({ title: 'Discard this confirmation?', body: "What you've filled in hasn't been sent.", confirmLabel: 'Discard', tone: 'danger' }))) return;
    setForm(INIT); setErrors({}); setOpen(false);
  };

  const send = async () => {
    const errs = {};
    if (!form.email.trim()) errs.email = 'Enter the contact’s email address';
    else { const m = checkEmail(form.email); if (m) errs.email = m; }
    const ph = checkUkPhone(form.phone); if (ph) errs.phone = ph;
    const pc = checkPostcode(form.postcode); if (pc) errs.postcode = pc;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setManualLink(null);
    try {
      const res = await apiFetch('/api/contractor/client-onboarding', { method: 'POST', body: JSON.stringify({ ...form, email: form.email.trim(), expires_days: Number(form.expires_days), client_id: form.client_id || null }) });
      if (res?.emailed) toast.success(`Confirmation link sent to ${form.email.trim()}`);
      else if (await copyText(res?.url)) toast.info('No mailbox is connected, so nothing was emailed — the confirmation link is copied to your clipboard to send yourself.', { autoClose: 9000 });
      else { setManualLink(res?.url || null); toast.info('No mailbox is connected, so nothing was emailed — copy the link below and send it yourself.'); }
      setForm(INIT); setOpen(false); load();
    } catch (err) { toast.error(`Couldn't create the confirmation link: ${apiErrorMessage(err)}`); }
    finally { setBusy(false); }
  };

  const withdraw = async (i) => {
    const ok = await confirm({
      title: `Withdraw the link sent to ${i.email}?`,
      body: 'It stops working straight away. You can send a new confirmation later.',
      confirmLabel: 'Withdraw link', tone: 'danger',
    });
    if (!ok) return;
    const before = invites;
    setInvites((p) => p.map((x) => (x.id === i.id ? { ...x, status: 'revoked' } : x)));
    try { await apiFetch(`/api/contractor/client-onboarding/${i.id}`, { method: 'DELETE' }); toast.success('Link withdrawn'); }
    catch (e) { setInvites(before); toast.error(`Couldn't withdraw that link: ${apiErrorMessage(e)}`); }
  };
  const copyLink = async (url) => {
    if (await copyText(url)) toast.success('Link copied');
    else { setManualLink(url); toast.info('Your browser blocked copying — the link is shown above to copy by hand.'); }
  };

  return (
    <section className="cw-card cw-card-pad" aria-labelledby="cw-onb-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 id="cw-onb-title" className="cw-h3" style={{ margin: 0 }}>
            <i className="fa-solid fa-user-check" style={{ color: 'var(--cw-blue)', marginRight: 6 }} aria-hidden="true" />
            Client onboarding
            {invites.length > 0 && <span className="cw-count-pill">{invites.length}</span>}
          </h3>
          <p className="cw-meta" style={{ margin: '2px 0 0' }}>
            Fill in what you already know — the client just checks it and confirms. No form for them to complete.
          </p>
        </div>
        {!open && (
          <button type="button" className="cw-btn cw-btn-primary" onClick={() => setOpen(true)}>
            <i className="fa-solid fa-plus" aria-hidden="true" /> New confirmation
          </button>
        )}
      </div>

      {manualLink && (
        <div role="status" style={{ marginBottom: 12 }}>
          <label className="cw-label" htmlFor="cw-onb-manual">Confirmation link to send</label>
          <input id="cw-onb-manual" readOnly className="cw-input" value={manualLink} onFocus={(e) => e.target.select()} style={{ fontFamily: 'ui-monospace, monospace' }} />
        </div>
      )}

      {open && (
        <form noValidate onSubmit={(e) => { e.preventDefault(); send(); }} style={{ border: '1px solid var(--cw-border)', borderRadius: 12, padding: 14, marginBottom: 14, background: 'var(--cw-surface-alt)' }}>
          {clients.length > 0 && (
            <Field label="Start from a saved client (optional)">
              <select value={form.client_id} onChange={(e) => pickClient(e.target.value)}>
                <option value="">Not linked to a saved client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }} className="cw-field-grid">
            <Field label="Company name"><input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} autoComplete="organization" /></Field>
            <Field label="Contact name"><input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} autoComplete="name" /></Field>
            <Field label="Email" required error={errors.email}><input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="name@company.co.uk" /></Field>
            <Field label="Phone" error={errors.phone}><input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
            <Field label="Address"><input value={form.address} onChange={(e) => set('address', e.target.value)} autoComplete="street-address" /></Field>
            <Field label="Postcode" error={errors.postcode}><input value={form.postcode} onChange={(e) => set('postcode', e.target.value)} autoCapitalize="characters" /></Field>
            <Field label="Link expires after" hint="The client must confirm before then">
              <select value={form.expires_days} onChange={(e) => set('expires_days', e.target.value)}>
                {EXPIRY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="submit" className="cw-btn cw-btn-primary" disabled={busy} aria-busy={busy || undefined}>
              <i className="fa-solid fa-paper-plane" aria-hidden="true" /> {busy ? 'Sending…' : 'Send for confirmation'}
            </button>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={cancel} disabled={busy}>Cancel</button>
          </div>
        </form>
      )}

      {error ? <ErrorState compact thing="onboarding confirmations" error={error} onRetry={load} retrying={loading} />
        : loading && !invites.length ? <div role="status" className="cw-meta">Loading…</div>
        : invites.length === 0 ? (
          <EmptyState compact icon="fa-solid fa-user-check" title="No confirmations sent yet" />
        ) : (
          <div className="cw-share-tablewrap">
            <table className="cw-share-table">
              <caption className="cw-sr-only">Client onboarding confirmations ({plural(invites.length, 'confirmation')})</caption>
              <thead><tr><th scope="col">Client</th><th scope="col" style={{ textAlign: 'left' }}>Sent to</th><th scope="col" style={{ textAlign: 'left' }}>Status</th><th scope="col" style={{ textAlign: 'left' }}>Dates</th><th scope="col"><span className="cw-sr-only">Actions</span></th></tr></thead>
              <tbody>
                {invites.map((i) => {
                  const [tone, label] = chipFor(i);
                  const live = i.status === 'sent' && !isExpired(i);
                  return (
                    <tr key={i.id}>
                      <td style={{ fontWeight: 600 }}>{i.client_name || i.prefill?.companyName || 'Not named'}</td>
                      <td style={{ color: 'var(--cw-text-muted)', overflowWrap: 'anywhere' }}>{i.email}</td>
                      <td><Chip tone={tone} label={label} dot /></td>
                      <td style={{ color: 'var(--cw-text-muted)' }}>
                        {i.status === 'confirmed' ? `Confirmed ${formatDate(i.confirmed_at)}`
                          : i.expires_at ? `${isExpired(i) ? 'Expired' : 'Expires'} ${formatDate(i.expires_at)}` : `Sent ${formatDate(i.sent_at)}`}
                      </td>
                      <td className="tick" style={{ whiteSpace: 'nowrap' }}>
                        {live && (
                          <>
                            <button type="button" className="cw-btn-link" onClick={() => copyLink(i.url)} aria-label={`Copy the link sent to ${i.email}`}>Copy link</button>
                            <button type="button" className="cw-btn-link danger" style={{ marginLeft: 12 }} onClick={() => withdraw(i)} aria-label={`Withdraw the link sent to ${i.email}`}>Withdraw</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </section>
  );
};

export default ClientOnboarding;
